// ssrc/features/townBudget/components/programs/Program_ChargesForServices.jsx

import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Town Program: Charges for Services
 *
 * Firestore docId should match your stored doc:
 *   revenues_charges_for_services
 */
export const PROGRAM_CHARGES_FOR_SERVICES_CONFIG = {
  fullName: "Charges for Services",
  docId: "revenues_charges_for_services",

  fieldDefs: [
    {
      key: "planningZoning",
      label: "Planning & Zoning",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Planning & Zoning" }],
      group: "planning",
      defaultVisible: true,
    },
    {
      key: "townClerkRecordingFees",
      label: "Town Clerk Recording Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Town Clerk Recording Fees" }],
      group: "townClerk",
      defaultVisible: true,
    },
    {
      key: "townClerkConveyanceFee",
      label: "Town Clerk Conveyance Fee",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Town Clerk Conveyance Fee" }],
      group: "townClerk",
      defaultVisible: true,
    },
    {
      key: "educCommunityServFees",
      label: "Educ/Community Serv Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Educ/Community Serv Fees" }],
      group: "education",
      defaultVisible: false,
    },
    {
      key: "solidWasteTipFees",
      label: "Solid Waste Tip Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Solid Waste Tip Fees" }],
      group: "publicWorks",
      defaultVisible: true,
    },
    {
      key: "sewerInspectionFees",
      label: "Sewer Inspection Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Sewer Inspection Fees" }],
      group: "publicWorks",
      defaultVisible: false,
    },
    {
      key: "parksRecSwimmingFees",
      label: "Parks/Rec Swimming Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Parks/Rec Swimming Fees" }],
      group: "parksRecreation",
      defaultVisible: false,
    },
    {
      key: "parksRecProgramFees",
      label: "Parks/Rec Program Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Parks/Rec Program Fees" }],
      group: "parksRecreation",
      defaultVisible: false,
    },
    {
      key: "subdivisionOtInspection",
      label: "Subdivision OT Inspection",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Subdivision OT Inspection" }],
      group: "planning",
      defaultVisible: false,
    },
    {
      key: "fireWatchServices",
      label: "Fire Watch Services",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Fire Watch Services" }],
      group: "healthSafety",
      defaultVisible: false,
    },
    {
      key: "healthSoilTests",
      label: "Health Soil Tests",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Health Soil Tests" }],
      group: "healthSafety",
      defaultVisible: false,
    },
    {
      key: "seniorServicePrograms",
      label: "Senior Service Programs",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Senior Service Programs" }],
      group: "communityServices",
      defaultVisible: false,
    },
    {
      key: "seniorNutritionProgram",
      label: "Senior Nutrition Program",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Senior Nutrition Program" }],
      group: "communityServices",
      defaultVisible: false,
    },
    {
      key: "libraryFines",
      label: "Library Fines",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Library Fines" }],
      group: "library",
      defaultVisible: false,
    },
    {
      key: "notaryServices",
      label: "Notary Services",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Notary Services" }],
      group: "townClerk",
      defaultVisible: false,
    },
    {
      key: "passportProcessing",
      label: "Passport Processing",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "Passport Processing" }],
      group: "townClerk",
      defaultVisible: false,
    },
    {
      key: "evChargingFees",
      label: "EV Charging Fees",
      valueType: "currency",
      sources: [{ block: "Charges for Services", key: "EV Charging Fees" }],
      group: "publicWorks",
      defaultVisible: false,
    },
  ],

  tables: [
    {
      id: "adopted",
      title: "Charges for Services – Approved",
      sectionKey: "Approved",
      fields: [
        "planningZoning",
        "townClerkRecordingFees",
        "townClerkConveyanceFee",
        "educCommunityServFees",
        "solidWasteTipFees",
        "sewerInspectionFees",
        "parksRecSwimmingFees",
        "parksRecProgramFees",
        "subdivisionOtInspection",
        "fireWatchServices",
        "healthSoilTests",
        "seniorServicePrograms",
        "seniorNutritionProgram",
        "libraryFines",
        "notaryServices",
        "passportProcessing",
        "evChargingFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },
        planningZoning: { minWidth: "160px" },
        townClerkRecordingFees: { minWidth: "200px" },
        townClerkConveyanceFee: { minWidth: "210px" },
        educCommunityServFees: { minWidth: "200px" },
        solidWasteTipFees: { minWidth: "180px" },
        sewerInspectionFees: { minWidth: "180px" },
        parksRecSwimmingFees: { minWidth: "190px" },
        parksRecProgramFees: { minWidth: "180px" },
        subdivisionOtInspection: { minWidth: "190px" },
        fireWatchServices: { minWidth: "160px" },
        healthSoilTests: { minWidth: "150px" },
        seniorServicePrograms: { minWidth: "190px" },
        seniorNutritionProgram: { minWidth: "200px" },
        libraryFines: { minWidth: "120px" },
        notaryServices: { minWidth: "140px" },
        passportProcessing: { minWidth: "170px" },
        evChargingFees: { minWidth: "140px" },
      },
    },
    {
      id: "actual",
      title: "Charges for Services – Actual",
      sectionKey: "Actual",
      fields: [
        "planningZoning",
        "townClerkRecordingFees",
        "townClerkConveyanceFee",
        "educCommunityServFees",
        "solidWasteTipFees",
        "sewerInspectionFees",
        "parksRecSwimmingFees",
        "parksRecProgramFees",
        "subdivisionOtInspection",
        "fireWatchServices",
        "healthSoilTests",
        "seniorServicePrograms",
        "seniorNutritionProgram",
        "libraryFines",
        "notaryServices",
        "passportProcessing",
        "evChargingFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },
        planningZoning: { minWidth: "160px" },
        townClerkRecordingFees: { minWidth: "200px" },
        townClerkConveyanceFee: { minWidth: "210px" },
        educCommunityServFees: { minWidth: "200px" },
        solidWasteTipFees: { minWidth: "180px" },
        sewerInspectionFees: { minWidth: "180px" },
        parksRecSwimmingFees: { minWidth: "190px" },
        parksRecProgramFees: { minWidth: "180px" },
        subdivisionOtInspection: { minWidth: "190px" },
        fireWatchServices: { minWidth: "160px" },
        healthSoilTests: { minWidth: "150px" },
        seniorServicePrograms: { minWidth: "190px" },
        seniorNutritionProgram: { minWidth: "200px" },
        libraryFines: { minWidth: "120px" },
        notaryServices: { minWidth: "140px" },
        passportProcessing: { minWidth: "170px" },
        evChargingFees: { minWidth: "140px" },
      },
    },
  ],

  chartTitle: "Charges for Services Line Charges for Services (Actual preferred)",
};

export function ProgramChargesForServices(props) {
  return (
    <ProgramBudget
      programId="revenues_charges_for_services"
      config={PROGRAM_CHARGES_FOR_SERVICES_CONFIG}
      {...props}
    />
  );
}
